import React, { Component } from 'react';
import GridLayout from 'react-grid-layout';
import Utils from './../../../../utils';
import GridUtils from './../../../../GridUtils';
import ReportSections from './../../constants/ReportSections';
import ExpandableSections from './../../constants/ExpandableSections';
import ReportElementIdentifiers from './../../constants/ReportElementIdentifiers';
import ScoreRange from './../../constants/ScoreRange';
import IntellectualCapabilityExtendedIcons from './../../constants/IntellectualCapabilityExtendedIcons';
import TeamRolePerspectives from './../../constants/TeamRolePerspectives';
import RiskIndication from './../RiskIndication';
import PersonaFitRoles from './../PersonaFitRozet/constants/PersonaFitRoles';
import ResultTextBlock from './../ResultTextBlock/ResultTextBlock';
import SmallScoreBarSection from './../SmallScoreBarSection/SmallScoreBarSection';
import SectionBlock from './../SectionBlock/SectionBlock';
import MultipleRankingSection from './../MultipleRankingSection/MultipleRankingSection';
import ConsultantTextSection from './../ConsultantTextSection/ConsultantTextSection';
import BulletStyle from './../TextBlock/constants/BulletStyle';
import RoundIcon from './../RoundIcon/RoundIcon';
import PersonaFitRozet from './../PersonaFitRozet/PersonaFitRozet';
import AppendixScoreBarSection from './../AppendixScoreBarSection/AppendixScoreBarSection';
import Reflection from './../Reflection/Reflection';
import WorkEnvironment from '../WorkEnvironment/WorkEnvironment';
import PersonalityGraph from '../PersonalityGraph';
import FitMatch from '../FitMatch';
import ScoreBarTypes from './../../constants/ScoreBarTypes';
import ScoreDisplayTypes from './../../constants/ScoreDisplayTypes';
import ProductTypes from './../../constants/ProductTypes';
import ReportHelper from './../../helpers/ReportHelper';
import ExpandText from './../ExpandText/ExpandText';
import ScoreBarNG from './../ScoreBarNG/ScoreBarNG';
import CompetencyProfileExtended from './../CompetencyProfileExtended/CompetencyProfileExtended';
import CompetencyIcon from './../CompetencyProfileExtended/components/CompetencyIcon/CompetencyIcon';
import ReportSectionHeading from './../ReportSectionHeading/ReportSectionHeading';
import { Toxics } from '../Toxics';
import FooterBar from '../SectionBlock/components/FooterBar/FooterBar';
import DecisionModuleOutcome from '../DecisionModule/components/DecisionModuleOutcome/DecisionModuleOutcome';
import { CriticalThinking } from '../CriticalThinking/CriticalThinking';
import { LSRConclusion } from '../LSRConclusion/LSRConclusion';
import { Placeholder } from '../Placeholder/Placeholder';
import { EditableReportSection } from '../EditableReportSection/EditableReportSection';
import { DealingWithResistance } from '../DealingWithResistance/DealingWithResistance';
import { WorkSafelyScores } from '../AddOns/WorkSafely';
import { EditableCompetencyProfile } from '../EditableReportSection/components/EditableCompetencyProfile/EditableCompetencyProfile';
import icons from './../../style/icons.scss';
import style from './style/grid.scss';

class Grid extends Component {
    constructor(props) {
        super(props);

        this.state = {
            gridLayout: [],
            expandableSections: [], // handles of sections that are expandable (texts)
            reflectionTextValue: '',
            displayForTextArea: 'none',
            displayForText: 'block'
        };

        this.gridItemRefs = [];

        // state of the expandable sections
        this.expandableSectionsCalculated = false;
        this.expandableSectionsToCheck = {};
        this.editableCompetencySectionResized = false;

        this.buildReportLayout = this.buildReportLayout.bind(this);
        this.buildReportGridContent = this.buildReportGridContent.bind(this);
        this.buildReportGridItem = this.buildReportGridItem.bind(this);
        this.resizeSection = this.resizeSection.bind(this);
        this.checkExpandableSections = this.checkExpandableSections.bind(this);
        this.resizeCompetencyProfile = this.resizeCompetencyProfile.bind(this);

        // to ensure generated Rozets have unique keys and will render properly (Safari), the key needs to
        // be incremented time they get created (this happens when opening / closing detail pages for example)
        this.personaFitRozetIndex = 0;
    }

    componentDidMount() {
        const expandableSections = [];

        // render the components off-screen to see which components are expandable
        // i is the handle name
        this.props.reportData.config.forEach(sectionConfig => {
            if (~ExpandableSections.indexOf(sectionConfig.i)) {
                expandableSections.push(sectionConfig);
            }
        });

        this.expandableSectionsToCheck = expandableSections;

        // build and render the layout
        this.buildReportLayout();
    }

    componentDidUpdate() {

        // check that the report is loaded and sections are rendered (refs are available)
        if (this.props.reportData.loaded && this.gridItemRefs && Object.keys(this.gridItemRefs).length && !this.expandableSectionsCalculated) {
            this.expandableSectionsCalculated = true;
            this.checkExpandableSections(this.expandableSectionsToCheck);
            this.resizeCompetencyProfile();
        }
    }

    componentWillUnmount() {
        try {
            document.querySelector(`#${ReportElementIdentifiers.REPORT_MAIN}`).removeEventListener('scroll', this.props.handleScroll);
            document.querySelector(`#${ReportElementIdentifiers.REPORT_DETAIL}`).removeEventListener('scroll', this.props.handleScroll);
        } catch (error) {

            // ignore
        }
    }

    // the editable competency section requires automatic resizing
    resizeCompetencyProfile() {
        const { reportData } = this.props;
        const competencyProfileSection = reportData.sections[ReportSections.COMPETENCY_PROFILE];
        const competencyProfileIndex = reportData.config.findIndex(item => item.i === ReportSections.COMPETENCY_PROFILE);

        if (
            this.editableCompetencySectionResized ||
            !this.gridItemRefs[ReportSections.COMPETENCY_PROFILE] ||
            !competencyProfileSection ||
            !competencyProfileIndex
        ) {
            return;
        }

        this.resizeSection(null, competencyProfileIndex, ReportSections.COMPETENCY_PROFILE);
        this.editableCompetencySectionResized = true;
    }


    checkExpandableSections(sectionConfigs) {
        const promises = [];

        sectionConfigs.forEach((sectionConfig, index) => {
            promises.push(GridUtils.isExpandable(
                sectionConfig,
                this.buildReportGridItem(sectionConfig.i, index),
                this.gridItemRefs[sectionConfig.i].getBoundingClientRect().width,
                this.props.ReportGridConfig.layout.rowHeight,
                this.props.ReportGridConfig.layout.marginY
            ));
        });

        Promise.all(promises).then(responses => {
            const expandableSections = [];

            for (let i = 0; i < responses.length; i++) {
                const sectionConfig = sectionConfigs[i];
                const shouldExpand = responses[i];

                if (shouldExpand) {
                    expandableSections.push(sectionConfig.i);
                }
            }

            this.setState({
                expandableSections
            });

            // To set competency profile expanded by default
            this.expandSection('competencyProfile');
        });
    }

    expandSection(sectionHandle) {
        const containerWidth = this.gridItemRefs[sectionHandle]?.getBoundingClientRect().width;

        if (!containerWidth) {
            return;
        }

        let index;

        for (index = 0; index <= this.state.gridLayout.length; index++) {
            if (this.state.gridLayout[index].i === sectionHandle) {
                break;
            }
        }

        if (index) {
            const gridElement = this.buildReportGridItem(sectionHandle, index, true);

            // Disable expand for editable sections
            if (gridElement?.props?.children?.props?.EditableReportContext) {
                return;
            }

            GridUtils.resizeToDynamicHeight(
                this.state.gridLayout,
                index,
                gridElement,
                containerWidth,
                this.props.ReportGridConfig.layout.rowHeight,
                this.props.ReportGridConfig.layout.marginY,
                this.props.ReportGridConfig.layout.sectionVerticalResizeMargin
            ).then(gridItems => {
                this.setState({
                    gridLayout: gridItems
                });
            });
        }
    }

    resizeSection(event, index, sectionHandle) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        const containerWidth = this.gridItemRefs[sectionHandle].getBoundingClientRect().width;

        // note that expanded is set to true so the section renders with the ExpandText expanded
        const gridElement = this.buildReportGridItem(sectionHandle, index, true);
        GridUtils.resizeToDynamicHeight(
            this.state.gridLayout,
            index,
            gridElement,
            containerWidth,
            this.props.ReportGridConfig.layout.rowHeight,
            this.props.ReportGridConfig.layout.marginY,
            this.props.ReportGridConfig.layout.sectionVerticalResizeMargin
        ).then(gridItems => {
            const competencyProfileIndex = gridItems.findIndex(item => item.i === 'competencyProfile');

            // editable competency section requires arond 25% extra height that cannot be measured traditionally
            if (competencyProfileIndex !== -1 && this.props.allowedToWriteReport) {
                gridItems[competencyProfileIndex].h += gridItems[competencyProfileIndex].h / 4;
            }

            this.setState({
                gridLayout: gridItems
            });
        });
    }

    /**
     * Calculate each report section's y and x axis by its width if all the items have the order value (so that the old reports can be displayed properly).
     * Assuming the max row width is 12, add the width of each section and insert it to fit the total.
     * The value of y axis is incremented by one for each row.
     * @param {Object} reportConfig - config object of report
     * @returns {Object} config object of report with calculated y axis
     */
    organizeGridItems(reportConfig) {

        // check if all the items have the order value
        const filterConfig = reportConfig.every(item => item.order);

        if (filterConfig) {
            const maxWidth = 12;
            let yAxis = 0;
            let xAxis = 0;
            let totalRowWidth = 0;

            reportConfig.sort((a, b) => a.order - b.order);

            reportConfig.forEach((gridItem, i) => {
                if ((gridItem.w + totalRowWidth) <= maxWidth) {
                    totalRowWidth += gridItem.w;
                    if (i > 0) {
                        xAxis += reportConfig[i - 1].w;
                    }
                } else {
                    let itemPos = gridItem.x;
                    let itemI = i;
                    let rowH = gridItem.h;

                    while (itemPos > 0) {
                        itemI--;
                        itemPos = reportConfig[itemI].x;

                        if (reportConfig[itemI].h > rowH) {
                            rowH = reportConfig[itemI].h;
                        }
                    }

                    if (reportConfig[i - 1].h + gridItem.h > rowH) {
                        totalRowWidth = gridItem.w;
                        xAxis = 0;
                        yAxis += 1;
                    }
                }

                gridItem.y = yAxis;
                gridItem.x = xAxis;
            });
        }

        return reportConfig;
    }

    buildReportLayout() {
        const { reportData: { config } } = this.props;

        const layoutConfig = this.organizeGridItems(config);

        this.setState({
            gridLayout: layoutConfig
        });
    }

    buildReportGridContent() {
        const { gridLayout } = this.state;
        const gridItems = [];

        if (!gridLayout || !gridLayout.length) {
            return null;
        }

        gridLayout.forEach((gridItem, index) => {
            gridItems.push(this.buildReportGridItem(gridItem.i, index));
        });

        return gridItems;
    }

    getInitials(participant) {
        if (!participant.firstName || !participant.lastName) {
            return null;
        }

        const firstNameInitial = participant.firstName.slice(0, 1).toUpperCase();
        const lastNameInitial = participant.lastName.slice(0, 1).toUpperCase();

        return `${firstNameInitial}${lastNameInitial}`;
    }

    getPreferredScore(scoreObj) {
        if (scoreObj.scoreByConsultant) {
            return scoreObj.scoreByConsultant;
        }

        if (scoreObj.scoreTypes && scoreObj.scoreTypes.norm5) {
            return scoreObj.scoreTypes.norm5;
        }

        if (scoreObj.score) {
            return scoreObj.score;
        }

        return null;
    }

    getCutOffEvaluation(score, cutOff, hasNotLevelText, hasLevelText) {
        if (!score || !cutOff) {
            return null;
        }

        return <p>
            { score < cutOff
                ? hasNotLevelText
                : hasLevelText
            }
        </p>;
    }

    buildReportGridItem(handle, index, expanded = false) {
        let gridItem = null,
            bulletStyle = null,
            hybridDevelopmentAdvice = false,
            riskContent = [],
            introHeading = '',
            profileIntroHeader = '',
            fitMatch = {},
            personalityString = '',
            ambitionString = '',
            consultantTextSectionContent = '',
            scoreBarType = '',
            norm5Score = '',
            smallScoreBarSectionRootClasses = '';

        const { i18nParticipation, reportData, showReportOverlay, languageId, forwardToNextPhase, allowedToWriteReport, EditableReportContext } = this.props;
        const section = reportData.sections[handle];
        const participantLanguage = reportData.participant.language.replace('-', '_');
        let participantLevel = null;

        if (reportData.participant.educationLevel && i18nParticipation[`report_tested_level_${reportData.participant.educationLevel}`]) {
            participantLevel = <div className={ style.testedLevel__intellectualCapabilityExtended }>{ i18nParticipation.report_tested_level } { i18nParticipation[`report_tested_level_${reportData.participant.educationLevel}`] }</div>;
        }

        // if allowed to write report and section allows editing, show editable report section
        if (allowedToWriteReport && section.editable) {
            return <div
                key={ handle }
                ref={ elementRef => {
                    this.gridItemRefs[handle] = elementRef;
                }}
                className={ style.responsive_wide }
                id={handle}
            ><EditableReportSection
                reportData={reportData}
                i18n={i18nParticipation}
                section={section}
                basedOn={section.basedOn || i18nParticipation.report_section_based_on_written_by_consultant}
                saveInterval={this.props.saveInterval}
                EditableReportContext={EditableReportContext}
                classes={{
                    badge: {
                        root: style.badge__sectionBlock,
                        figure: icons[`badgeFigure__${handle}`]
                    }
                }}
                initials={this.getInitials(reportData.participant)}
            /></div>;
        }

        // if allowed to show placeholder and section is a placeholder, show placeholder
        if (section.placeholder) {
            return <div
                key={handle}
                ref={elementRef => {
                    this.gridItemRefs[handle] = elementRef;
                }}
                className={ style.responsive_wide }
            ><Placeholder
                i18nParticipation={i18nParticipation}
                title={section.title}
                handle={handle}
                basedOn={i18nParticipation.report_section_based_on_written_by_consultant}
                classes={{
                    root: style.consultantTextSection,
                    roundIcon: {
                        root: style.scoreRoundIcon__root,
                        roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                    },
                    roundIconFigureWrapper__withExpandIcon: style.consultantTextSection__roundIconFigureWrapperExpandIcon,
                    badge: {
                        root: style.badge__sectionBlock,
                        figure: icons[`badgeFigure__${handle}`]
                    }
                }}
            />
            </div>;
        }

        const basedOnText = section.basedOn || i18nParticipation.report_section_based_on_default_text;

        switch (handle) {
            case ReportSections.DECISION_MODULE:
                gridItem = <div
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                >
                    <SectionBlock
                        classes={{
                            root: style.scoreBlockWhite,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__decisionModuleOutcome
                            }
                        }}
                    >
                        <span className={style.sectionBlock_subtitle}>{ basedOnText }</span>
                        <h3 className={style.sectionBlock_title}>{section.title}</h3>
                        <DecisionModuleOutcome
                            type={section.type}
                            outcome={section.outcome}
                            translatedOutcome={section.translatedOutcome}
                            outcomeHeader={section.outcomeHeader}
                            outcomeText={section.outcomeText}
                            intellectualCapability={section.intellectualCapability}
                            competencies={section.competencies}
                            introduction={section.introduction}
                            languageId={participantLanguage}
                            i18n={i18nParticipation}
                            selfForwardingToNextPhase={section.selfForwardingToNextPhase}
                            forwardToNextPhase={forwardToNextPhase}
                            participantId={reportData?.participant?.id}
                        />
                    </SectionBlock>
                </div>;
                break;
            case ReportSections.INTELLECTUAL_CAPABILITY_MBO:
                const thinkingCapacityscores = [];

                if (section.scores) {
                    section.scores.forEach((score, thinkingCapacityIndex) => {
                        if (score.handle === 'gMatAbility' && section.scores.length > 1) {
                            return;
                        }

                        /* styling for tested level */
                        let containerClasses = `${style.thinkingCapacityScoreContainer}`;

                        // check for +1 since index starts at 0
                        if (section.mboLevel && section.mboLevel === (thinkingCapacityIndex + 1)) {

                            /* styling for active tested level */
                            containerClasses += ` ${style.thinkingCapacityScoreContainer__active}`;

                            if (section.cutOff) {

                                /* styling for active tested level with a score lower than cut-off */
                                if (score.scoreTypes && score.scoreTypes.norm5 && score.scoreTypes.norm5 < section.cutOff) {
                                    containerClasses += ` ${style.outside_cut_off}`;
                                } else if (score.score < section.cutOff) {
                                    containerClasses += ` ${style.outside_cut_off}`;
                                }
                            }
                        }

                        thinkingCapacityscores.push(
                            <React.Fragment key={`thinkingCapacity-${thinkingCapacityIndex}`}>
                                <div className={ containerClasses }>
                                    { section.mboLevel && section.mboLevel === (thinkingCapacityIndex + 1)
                                        ? <span className={ style.thinkingCapacityTestedLevel }>{ i18nParticipation.report_tested_level_indicator }</span>
                                        : '' }
                                    <header className={ style.thinkingCapacityScoreHeader }>
                                        <CompetencyIcon handle={ `icon_thinkingCapacity_${score?.handle}` } />
                                        <section>
                                            <h4>{ score?.name }</h4>
                                            <span>{ score?.basedOn }</span>
                                        </section>
                                    </header>
                                    <ScoreBarNG
                                        scoreMin={ ScoreRange.INTELLECTUAL_CAPABILITY_EXTENDED.MIN }
                                        scoreMax={ ScoreRange.INTELLECTUAL_CAPABILITY_EXTENDED.MAX }
                                        evaluation={ score?.evaluation }
                                        scoreValue={
                                            score?.scoreTypes && score?.scoreTypes.norm5
                                                ? score?.scoreTypes.norm5
                                                : score?.score
                                        }
                                        fill={ false }
                                        scoreBarType={ ScoreBarTypes.CUTOFF }
                                        disabled={ true }
                                        classes={{
                                            root: style.scoreBarIntellectualCapabilityExtended__root
                                        }}
                                        digits={ 2 }
                                        valueLabelDisplay={ section.cutOff ? 'on' : 'off' }
                                        cutOff={ section.cutOff || '' }
                                    />
                                    { section.cutOff
                                        ? this.getCutOffEvaluation(
                                            this.getPreferredScore(score),
                                            section.cutOff,
                                            i18nParticipation.report_tested_level_indicator_text_failed,
                                            i18nParticipation.report_tested_level_indicator_text_success
                                        ) : score?.evaluation ? <span className={ style.thinkingCapacityEvaluation }>{score?.evaluation}</span> : null
                                    }
                                </div>
                            </React.Fragment>
                        );
                    });
                }

                gridItem = <div
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    role="button"
                    tabIndex="-1"
                    className={ style.responsive_half }
                    id={ handle }
                >
                    <React.Fragment>

                        <SectionBlock classes={{
                            root: style.scoreBlockWhite,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__thinkingCapacity
                            }
                        }}>
                            <span className={ style.thinkingCapacitySubTitle }>{ basedOnText }</span>
                            <h3 className={ style.thinkingCapacityTitle }>{ i18nParticipation.report_thinking_capacity_what_is_your_thinking_capacity }</h3>

                            <ExpandText
                                text={ section.introduction }
                                classes={{
                                    root: style.expandText__thinkingCapacity
                                }}
                                resize={ event => this.resizeSection(event, index, handle) }
                                index={ 1 }
                                handle={ 'intellectualCapability__cutOff' }
                                expanded={ false }
                                disabled={ false }
                            />
                            <div className={ style.thinking_capacity_content_wrapper }>
                                { thinkingCapacityscores }
                            </div>
                        </SectionBlock>
                    </React.Fragment>
                </div>;

                break;

            case ReportSections.COLLABORATION:
            case ReportSections.WORK_APPROACH:
            case ReportSections.FLEXIBILITY_TO_CHANGE:
                gridItem = <div
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_half }
                    id={ handle }
                >
                    <SectionBlock
                        classes={{
                            root: style.scoreBlockWhite,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons[`badgeFigure__${handle}`]
                            }
                        }}
                    >
                        <span className={ style.sectionBlock_subtitle }>{ basedOnText }</span>
                        <MultipleRankingSection
                            classes={{
                                root: style.multipleRankingSection__workProfile,
                                output: style.multipleRankingSection__output,
                                roundIcon: {
                                    roundIconFigureWrapper: style.hidden
                                },
                                contentWrapper: style.sectionBlock__noPadding
                            }}
                            iconText={ '' }
                            title={ section.title }
                        >
                            { section.scores.map(
                                (workProfileScore, i) => <AppendixScoreBarSection
                                    key={ `scale-${workProfileScore.name}-${i}` }
                                    scoreValue={ workProfileScore.score }
                                    scoreMin={ ScoreRange.APPENDIXES.MIN }
                                    scoreMax={ ScoreRange.APPENDIXES.MAX }
                                    lowLabel={ i18nParticipation[`report_${workProfileScore.lowLabel}`] || workProfileScore.lowLabel }
                                    highLabel={ i18nParticipation[`report_${workProfileScore.highLabel}`] || workProfileScore.highLabel }
                                    scoreName={ workProfileScore.name }
                                    scoreBarType={ ScoreBarTypes.BAR_GRAPH }
                                    classes={ {
                                        low: style.lowLabel__workProfile,
                                        high: style.highLabel__workProfile,
                                        fill: style.multipleRankingSectionScoreBar__fill,
                                        blank: style.multipleRankingSectionScoreBar__blank,
                                        scoreBlock: style.multipleRankingSectionScoreBar__scoreBlock__workProfile,
                                        categoryScore: style.hidden,
                                        barGraph: {
                                            root: style.barGraph__workingProfile
                                        }
                                    } }
                                />
                            ) }
                        </MultipleRankingSection>
                    </SectionBlock>
                </div>;

                break;

            case ReportSections.INTELLECTUAL_CAPABILITY_EXTENDED:
                gridItem = <div
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    role="button"
                    tabIndex="-1"
                    className={ style.intellectualCapabilityExtended }
                    id={ handle }
                >
                    <SectionBlock classes={{ root: style.scoreBlockWhite }}>
                        <h3>{ i18nParticipation.report_intellectual_capabilities }</h3>

                        <ExpandText
                            text={ section.introduction }
                            classes={{
                                root: style.expandText__intellectualCapabilityExtended
                            }}
                            resize={ event => this.resizeSection(event, index, handle) }
                            index={ index }
                            handle={ handle }
                            expanded={ expanded }
                        />

                        { participantLevel }

                        <section className={ style.intellectualCapabilityExtendedScores }>
                            <section>

                                { /* note: cant use reportSectionHeading component here or in the sub scales, since preFit header has different layout than usual */ }
                                <header className={ style.intellectualCapabilityExtendedScoresHeader }>
                                    <div className={ style.intellectualCapabilityExtended__mainScaleIcon }>
                                        <figure />
                                    </div>
                                    <section>
                                        <h3>{ i18nParticipation[`report_${Utils.camelCaseToSnakeCase(section.handle)}`] || section.handle }</h3>
                                        <span>{ basedOnText }</span>
                                    </section>
                                </header>

                                <p className={ style.definition__intellectualCapabilityExtended }>{ section.definition }</p>

                                <ScoreBarNG
                                    scoreMin={ ScoreRange.INTELLECTUAL_CAPABILITY_EXTENDED.MIN }
                                    scoreMax={ ScoreRange.INTELLECTUAL_CAPABILITY_EXTENDED.MAX }
                                    scoreValue={
                                        section.scoreTypes && section.scoreTypes.norm5
                                            ? section.scoreTypes.norm5
                                            : section.score
                                    }
                                    fill={ false }
                                    scoreBarType={ ScoreBarTypes.CUTOFF }
                                    disabled={ true }
                                    classes={{
                                        root: style.scoreBarIntellectualCapabilityExtended__root
                                    }}
                                    digits={ 2 }
                                    valueLabelDisplay={ section.cutOff ? 'on' : 'off' }
                                    cutOff={ section.cutOff || '' }
                                />
                                <span className={ style.scoreBarIntellectualCapabilityExtended__score }>
                                    { section.scoreTypes && section.scoreTypes.norm5
                                        ? section.scoreTypes.norm5
                                        : section.score
                                    }
                                </span>

                                { section.cutOff
                                    ? this.getCutOffEvaluation(
                                        this.getPreferredScore(section),
                                        section.cutOff,
                                        i18nParticipation.report_intellectual_capability_extended_has_not_requested_level,
                                        i18nParticipation.report_intellectual_capability_extended_has_requested_level
                                    ) : <p dangerouslySetInnerHTML={{ __html: section.evaluation }}/>
                                }

                            </section>

                            <aside>

                                { section.scores ? section.scores.map((score, iceIndex) =>

                                    <React.Fragment key={ iceIndex }>

                                        <header className={ style.intellectualCapabilityExtendedScoresHeader }>
                                            <RoundIcon
                                                classes={{
                                                    root: style.intellectualCapabilityExtended__scoreRoundIcon,
                                                    roundIconFigureWrapper: style.intellectualCapabilityExtended__roundIconFigureWrapper
                                                }}
                                                circleSize={ 1.75 }
                                                iconSize={ 1.5 }
                                                iconType={ IntellectualCapabilityExtendedIcons[score.handle] }
                                            />
                                            <section>
                                                <h3>{ i18nParticipation[`report_${Utils.camelCaseToSnakeCase(score.name)}`] || score.name }</h3>
                                                <span>{ score.basedOn }</span>
                                            </section>
                                        </header>

                                        <ExpandText
                                            text={ score.definition }
                                            classes={{
                                                root: style.expandText__intellectualCapabilityExtended
                                            }}
                                            resize={ event => this.resizeSection(event, index, handle) }
                                            index={ index }
                                            handle={ handle }
                                            expanded={ true } // since there is an issue with multiple expand texts in a single report section, always show these texts expanded
                                            disabled={ true }
                                        />

                                        <ScoreBarNG
                                            scoreMin={ ScoreRange.INTELLECTUAL_CAPABILITY_EXTENDED.MIN }
                                            scoreMax={ ScoreRange.INTELLECTUAL_CAPABILITY_EXTENDED.MAX }
                                            scoreValue={
                                                score.scoreTypes && score.scoreTypes.norm5
                                                    ? score.scoreTypes.norm5
                                                    : score.score
                                            }
                                            fill={ false }
                                            scoreBarType={ ScoreBarTypes.CUTOFF }
                                            disabled={ true }
                                            classes={{
                                                root: style.scoreBarIntellectualCapabilityExtended__root
                                            }}
                                            digits={ 1 }
                                            valueLabelDisplay={ 'off' }
                                        />

                                        <span className={ style.scoreBarIntellectualCapabilityExtended__score }>
                                            { score.scoreTypes && score.scoreTypes.norm5
                                                ? score.scoreTypes.norm5
                                                : score.score
                                            }
                                        </span>

                                        { score.cutOff
                                            ? this.getCutOffEvaluation(
                                                this.getPreferredScore(score),
                                                score.cutOff,
                                                i18nParticipation.report_intellectual_capability_extended_has_not_requested_level,
                                                i18nParticipation.report_intellectual_capability_extended_has_requested_level
                                            ) : <p dangerouslySetInnerHTML={{ __html: score.evaluation }}/>
                                        }

                                        <br />
                                        <br />

                                    </React.Fragment>) : null }

                            </aside>
                        </section>
                    </SectionBlock>
                </div>;

                break;

            case ReportSections.INTELLECTUAL_CAPABILITY:

                norm5Score = null;

                if (section.scoreTypes && ReportHelper.getScoreType(section.scoreTypes, ScoreDisplayTypes.NORM5, 1)) {
                    norm5Score = ReportHelper.getScoreType(section.scoreTypes, ScoreDisplayTypes.NORM5, 1);
                }

                smallScoreBarSectionRootClasses = section.participantRequestedLevel ? style.hoverActive__extended : style.hoverActive;
                scoreBarType = !section.scoreByConsultant ? ScoreBarTypes.BAR_GRAPH : ScoreBarTypes.NORMAL;

                if (section.cutOff) {
                    scoreBarType = ScoreBarTypes.CUTOFF;
                    smallScoreBarSectionRootClasses = `${smallScoreBarSectionRootClasses} ${style.smallScoreBarSection__bright}`;
                }

                gridItem = <div
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    role="button"
                    tabIndex="-1"
                    onClick={ () => {
                        showReportOverlay(ReportSections.INTELLECTUAL_CAPABILITY);
                    }}
                    data-cy={ `summary ${handle} viewall` }
                    className={ style.responsive_half }
                    id={ handle }
                >
                    { section.cutOff
                        ? <React.Fragment>

                            <SectionBlock classes={{
                                root: style.scoreBlockWhite,
                                contentWrapper: style.contentWrapper__cutOff
                            }}>

                                <ReportSectionHeading
                                    title={ section.title }
                                    iconType={ 'desktop' }
                                    iconText={ basedOnText }
                                    introHeading={ false }
                                    classes={{
                                        roundIcon: {
                                            root: style.scoreRoundIcon__root,
                                            roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper__onBlue
                                        }
                                    }}
                                />

                                <ExpandText
                                    text={ section.definition }
                                    classes={{
                                        root: style.expandText__intellectualCapabilityExtended
                                    }}
                                    resize={ event => this.resizeSection(event, index, handle) }
                                    index={ 1 }
                                    handle={ 'intellectualCapability__cutOff' }
                                    expanded={ false }
                                    disabled={ false }
                                />

                                <header className={ style.intellectualCapabilityCutOff }>
                                    <div className={ style.intellectualCapabilityCutOff__mainScaleIcon }>
                                        <figure />
                                    </div>
                                    <section>
                                        <span dangerouslySetInnerHTML={{ __html: section.evaluation }}/>
                                    </section>
                                </header>

                                <ScoreBarNG
                                    scoreMin={ ScoreRange.INTELLECTUAL_CAPABILITY_EXTENDED.MIN }
                                    scoreMax={ ScoreRange.INTELLECTUAL_CAPABILITY_EXTENDED.MAX }
                                    scoreValue={
                                        section.scoreTypes && section.scoreTypes.norm5
                                            ? section.scoreTypes.norm5
                                            : section.score
                                    }
                                    fill={ false }
                                    scoreBarType={ ScoreBarTypes.CUTOFF }
                                    disabled={ true }
                                    classes={{
                                        root: style.scoreBarIntellectualCapabilityExtended__root__cutOff
                                    }}
                                    digits={ 1 }
                                    valueLabelDisplay={ 'on' }
                                    cutOff={ section.cutOff }
                                />
                                { this.getCutOffEvaluation(
                                    this.getPreferredScore(section),
                                    section.cutOff,
                                    i18nParticipation.report_intellectual_capability_extended_has_not_requested_level,
                                    i18nParticipation.report_intellectual_capability_extended_has_requested_level
                                ) }
                            </SectionBlock>
                        </React.Fragment>
                        : <SmallScoreBarSection
                            title={ section.title }
                            scoreMin={ ScoreRange.INTELLECTUAL_CAPABILITY.MIN }
                            scoreMax={ ScoreRange.INTELLECTUAL_CAPABILITY.MAX }
                            iconText={ basedOnText }
                            scoreLevelDescription={ section.scoreByConsultant && section.evaluationByConsultant
                                ? section.evaluationByConsultant
                                : section.evaluation
                            }
                            iconType={ section.scoreByConsultant ? 'user-edit' : 'desktop' }
                            participantRequestedLevel={ section.participantRequestedLevel || null }
                            classes={{
                                root: smallScoreBarSectionRootClasses,
                                barGraph: {
                                    root: style.barGraph__centeredScore__intellectualCapability,
                                    fill: style.barGraph__fill_on_blue
                                },
                                scoreBar: style.scoreBar,
                                scoreLevel: style.scoreLevel,
                                roundIcon: {
                                    root: style.scoreRoundIcon__root__onBlue,
                                    roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper__onBlue
                                }
                            }}

                            /* as an exception, the type of score bar depends on score by consultant being present */
                            scoreBarType={ scoreBarType }

                            /* the score value (used to fill in score bar sections) is set to the consultant score, with a fallback for category score */
                            scoreValue={ section.scoreByConsultant || section.score }

                            /* the display score (the visibile score) is either consultant, norm5 or category score (in that order */
                            displayScore={ section.scoreByConsultant || norm5Score || section.score }

                            autoScaleWidth={ false }

                            valueLabelDisplay={ section.cutOff ? 'on' : 'off' }
                        />
                    }
                </div>;

                break;

            case ReportSections.COMPETENCY_PROFILE:

                if (reportData.assessmentType === 'hybrid') {
                    gridItem = <div
                        key={handle}
                        ref={elementRef => {
                            this.gridItemRefs[handle] = elementRef;
                        }}
                        className={style.responsive_wide}
                        id={handle}
                    >
                        <SectionBlock
                            classes={{
                                root: `${style.scoreBlockWhite} ${style.sectionBlock__competencyProfileExtended}`,
                                badge: {
                                    root: style.badge__sectionBlock,
                                    figure: icons.badgeFigure__competencyProfile
                                }
                            }}
                        >
                            <span className={style.sectionBlock_subtitle}>{basedOnText}</span>
                            <h3 className={style.sectionBlock_title}>{section.title}</h3>
                            <div className={style.hybridCompetencyProfileWrapper}>
                                <EditableCompetencyProfile
                                    i18n={i18nParticipation}
                                    languageId={participantLanguage}
                                    saveCompetency={null}
                                    reportData={reportData}
                                    initials={this.getInitials(reportData.participant)}
                                />
                            </div>
                        </SectionBlock>
                    </div>;
                } else if (section.extended) {
                    gridItem = <div
                        key={handle}
                        ref={elementRef => {
                            this.gridItemRefs[handle] = elementRef;
                        }}
                        className={style.responsive_wide}
                        id={handle}
                    >
                        <SectionBlock
                            classes={{
                                root: `${style.scoreBlockWhite} ${style.sectionBlock__competencyProfileExtended}`,
                                badge: {
                                    root: style.badge__sectionBlock,
                                    figure: icons.badgeFigure__competencyProfile
                                }
                            }}
                        >
                            <span className={style.sectionBlock_subtitle}>{basedOnText}</span>
                            <h3 className={style.sectionBlock_title}>{section.title}</h3>
                            {section.introduction
                                ? <ExpandText
                                    text={section.introduction}
                                    classes={{
                                        root: style.expandText__reportSection__competencyProfileExtended
                                    }}
                                    resize={event => this.resizeSection(event, index, handle)}
                                    index={index}
                                    handle={handle}
                                    expanded={true}
                                />
                                : null}
                            <CompetencyProfileExtended
                                competencies={section.competencies}
                                openCompetencyExtendedModal={this.props.openCompetencyExtendedModal}
                                closeCompetencyExtendedModal={this.props.closeCompetencyExtendedModal}
                                i18n={i18nParticipation}
                                languageId={participantLanguage}
                                classes={{
                                    competencyProfileExtended: style.competencyProfileExtended
                                }}
                            />
                        </SectionBlock>
                    </div>;
                } else {
                    gridItem = <div
                        role="button"
                        tabIndex="-1"
                        key={handle}
                        ref={elementRef => {
                            this.gridItemRefs[handle] = elementRef;
                        }}
                        onClick={() => {
                            showReportOverlay(ReportSections.COMPETENCY_PROFILE);
                        }}
                        className={style.responsive_wide}
                        id={ handle }
                        data-cy={ `summary ${handle} viewall` }
                    >
                        <SectionBlock
                            classes={{
                                root: `${style.scoreBlockWhite} ${style.sectionBlock__withBadge}`,
                                badge: {
                                    root: style.badge__sectionBlock,
                                    figure: icons.badgeFigure__competencyProfile
                                }
                            }}
                        >
                            <MultipleRankingSection
                                faIconString={reportData.product.translationKey === ProductTypes.TALENT_FIT ? 'desktop' : 'user-edit'}
                                classes={{
                                    root: style.competencyProfile,
                                    roundIcon: {
                                        root: style.scoreRoundIcon__root,
                                        roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                    },
                                    contentWrapper: style.sectionBlock__noPadding
                                }}
                                title={i18nParticipation.report_competency_profile}
                                iconText={basedOnText}
                            >
                                {section.introduction
                                    ? <ExpandText
                                        text={section.introduction}
                                        classes={{
                                            root: style.expandText__reportSection
                                        }}
                                        resize={event => this.resizeSection(event, index, handle)}
                                        index={index}
                                        handle={handle}
                                        expanded={expanded}
                                    />
                                    : null}

                                {section.competencies.map((competency, competencyIndex) => {
                                    if (ReportHelper.shouldUseStarRanking(reportData.product.translationKey)) {

                                        // return Stars score bar section
                                        return <ScoreBarNG
                                            key={`star-${competency.name}-${competencyIndex}`}
                                            label={ReportHelper.getCompetencyName(competency, participantLanguage, i18nParticipation)}
                                            scoreValue={competency.score}
                                            classes={{
                                                active: style.starRanking__active,
                                                inactive: style.starRanking__inactive
                                            }}
                                            scoreLevelDescription={competency.label}
                                            scoreBarType={ScoreBarTypes.STARS}
                                            scoreDivisionMinimum={0.5}
                                            scoreMax={5}
                                        />;
                                    }

                                    // return appendix score bar section (with developability indicator if provided)
                                    return <AppendixScoreBarSection
                                        key={`scale-${competency.name}`}
                                        lowLabel={competency.label}
                                        scoreName={ReportHelper.getCompetencyName(competency, participantLanguage, i18nParticipation)}
                                        scoreValue={competency.score}
                                        scoreMin={ScoreRange.APPENDIXES.MIN}
                                        scoreMax={ScoreRange.APPENDIXES.MAX}
                                        scoreBarType={ScoreBarTypes.CROISSANT}
                                        classes={{
                                            low: style.lowLabel,
                                            fill: style.multipleRankingSectionScoreBar__fill,
                                            blank: style.multipleRankingSectionScoreBar__blank,
                                            scoreBlock: style.multipleRankingSectionScoreBar__scoreBlockCompetencies,
                                            scoreBar: style.multipleRankingSectionScoreBarForCompetencies
                                        }}
                                        languageId={languageId}
                                        developabilityLabel={competency.developabilityLabel || ''}
                                        developabilityScore={competency.developabilityScore || ''}
                                        scoreBarConfig={{
                                            activeBarHeight: 20
                                        }}
                                    />;
                                })
                                }

                            </MultipleRankingSection>
                        </SectionBlock>
                    </div>;
                }

                break;

            case ReportSections.HEXIT:
                gridItem = <div
                    key={handle}
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_half }
                    id={ handle }
                >
                    <React.Fragment>
                        <SectionBlock
                            classes={{
                                root: style.scoreBlockWhite,
                                badge: {
                                    root: style.badge__sectionBlock,
                                    figure: icons.badgeFigure__derailment
                                }
                            }}>

                            <div className={ style.derailmentExtendedScoresHeader }>
                                <p className={style.sectionBlock_subtitle}>
                                    { basedOnText }
                                </p>
                                <h3>{ section.title }</h3>
                            </div>

                            {i18nParticipation.report_derailment_risks_explanation
                                ? <ExpandText
                                    text={i18nParticipation.report_derailment_risks_explanation}
                                    classes={{
                                        root: style.expandText__derailmentExtended
                                    }}
                                    resize={event => this.resizeSection(event, index, handle)}
                                    index={index}
                                    handle={handle}
                                    expanded={expanded}
                                />
                                : null}

                            <Toxics
                                section={section}
                                i18nParticipation={i18nParticipation}
                                sectionIconClasses={{
                                    root: style.badge__sectionBlock,
                                    figure: icons.badgeFigure__derailment
                                }}
                            />
                        </SectionBlock>
                    </React.Fragment>
                </div>;
                break;

            case ReportSections.ABILITY_TO_CHANGE:
                gridItem = <div
                    role="button"
                    tabIndex="-1"
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    onClick={ () => {
                        showReportOverlay(ReportSections.ABILITY_TO_CHANGE);
                    }}
                    data-cy={ `summary ${handle} viewall` }
                    className={ style.responsive_half }
                    id={ handle }
                >
                    <SmallScoreBarSection
                        title={ section.title }
                        scoreMin={ ScoreRange.ABILITY_TO_CHANGE.MIN }
                        scoreMax={ ScoreRange.ABILITY_TO_CHANGE.MAX }
                        scoreValue={ section.score ? section.score : 0 }
                        iconText={ basedOnText }
                        iconType={ section.score ? 'user-edit' : null }
                        scoreLevelDescription={ section.evaluation }
                        classes={{
                            root: style.hoverActive,
                            scoreBar: style.scoreBar,
                            scoreLevel: style.scoreLevel,
                            roundIcon: {
                                root: style.scoreRoundIcon__root__onBlue,
                                roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper__onBlue
                            }
                        }}
                        autoScaleWidth={ false }
                        scoreBarType={ ScoreBarTypes.NORMAL }
                    />
                </div>;
                break;

            case ReportSections.SELECTION_ADVICE:
            case ReportSections.SELECTION_DEVELOPMENT_ADVICE: {

                let resultIndicatorClassIdentifier = '';
                let evaluation = '';
                let selectionText = '';
                let developmentText = '';
                let selectionTextHeader = '';
                let developmentTextHeader = '';

                if (handle === ReportSections.SELECTION_ADVICE) {
                    evaluation = section.scores[0]?.evaluation;
                    resultIndicatorClassIdentifier = section.scores[0]?.score;
                    selectionText = section.text;
                }

                if (handle === ReportSections.SELECTION_DEVELOPMENT_ADVICE) {
                    evaluation = section.scores[0]?.evaluation;
                    resultIndicatorClassIdentifier = section.scores[0]?.score;
                    selectionText = section.selectionText;
                    developmentText = section.developmentText;
                    selectionTextHeader = i18nParticipation.report_selection_development_advice_selection_text_header;
                    developmentTextHeader = i18nParticipation.report_selection_development_advice_development_text_header;
                }

                consultantTextSectionContent = <ResultTextBlock
                    classes={{
                        root: reportData?.assessmentType === 'hybrid' ? style.resultTextBlock__root : '',
                        resultIndicator: style[resultIndicatorClassIdentifier]
                    }}
                    evaluation={evaluation}
                >
                    <div className={style.adviceText}>
                        {selectionTextHeader && <h4 className={style.helperText}>{selectionTextHeader}</h4>}
                        <div dangerouslySetInnerHTML={{ __html: selectionText }}/>
                        {developmentTextHeader && <h4 className={style.helperText}>{developmentTextHeader}</h4>}
                        <div dangerouslySetInnerHTML={{ __html: developmentText }}/>
                    </div>
                </ResultTextBlock>;

                gridItem = <div
                    key={ handle }
                    tabIndex="-1"
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                >
                    <ConsultantTextSection
                        title={ section.title }
                        bulletStyle={ bulletStyle }
                        resize={ event => this.resizeSection(event, index, handle) }
                        iconText={ basedOnText }
                        isExpandable={ !!~this.state.expandableSections.indexOf(handle) }
                        expandLabel={ i18nParticipation.report_read_more }
                        shrinkLabel={ i18nParticipation.report_close }
                        classes={{
                            roundIcon: {
                                roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                            },
                            roundIconFigureWrapper__withExpandIcon: style.consultantTextSection__roundIconFigureWrapperExpandIcon,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__selectionAdvice
                            }
                        }}
                    >{ consultantTextSectionContent }</ConsultantTextSection>
                </div>;
                break;
            }

            // consultant texts
            case ReportSections.STRONG_POINTS:
            case ReportSections.POINTS_OF_ATTENTION:
            case ReportSections.DEVELOPMENT_ADVICE:
            case ReportSections.RESEARCH_QUESTION:
            case ReportSections.QUALITIES:

                hybridDevelopmentAdvice = reportData?.assessmentType === 'hybrid';
                bulletStyle = handle === ReportSections.RESEARCH_QUESTION ? BulletStyle.QUESTION_MARK : BulletStyle.REGULAR;

                gridItem = <div
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                >
                    <ConsultantTextSection
                        title={ section.title }
                        bulletStyle={ bulletStyle }
                        resize={ event => this.resizeSection(event, index, handle) }
                        iconText={ basedOnText }
                        text={ section.text }
                        isExpandable={ !hybridDevelopmentAdvice || !!~this.state.expandableSections.indexOf(handle) }
                        expandLabel={ i18nParticipation.report_read_more }
                        shrinkLabel={ i18nParticipation.report_close }
                        classes={{
                            root: hybridDevelopmentAdvice ? '' : style.consultantTextSection,
                            roundIcon: {
                                root: hybridDevelopmentAdvice ? '' : style.scoreRoundIcon__root,
                                roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                            },
                            roundIconFigureWrapper__withExpandIcon: style.consultantTextSection__roundIconFigureWrapperExpandIcon,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: hybridDevelopmentAdvice ? icons.badgeFigure__developmentAdvice__hybrid : icons[`badgeFigure__${handle}`]
                            }
                        }}
                        isPreCalculated={
                            (handle === ReportSections.STRONG_POINTS || handle === ReportSections.POINTS_OF_ATTENTION) &&
                            !reportData.consultant?.consultantName
                        }
                    />
                </div>;

                break;

            case ReportSections.RISK_INDICATION:
                if (reportData.sections && reportData.sections.riskIndication) {
                    introHeading = reportData.sections.riskIndication.introduction;

                    if (reportData.sections.riskIndication.scores[0].score) {

                        riskContent = <RiskIndication
                            score={ reportData.sections.riskIndication.scores[0] }
                        />;
                    }
                }

                gridItem = <div
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                >
                    <ConsultantTextSection
                        introHeading= { introHeading }
                        title={ section.title }
                        resize={ event => this.resizeSection(event, index, handle) }
                        iconText={ basedOnText }
                        isExpandable={ !!~this.state.expandableSections.indexOf(handle) }
                        expandLabel={ i18nParticipation.report_read_more }
                        shrinkLabel={ i18nParticipation.report_close }
                        content = { <React.Fragment>
                            { !introHeading && section.introduction
                                ? <ExpandText
                                    text={ section.introduction }
                                    classes={{
                                        root: style.expandText__reportSection
                                    }}
                                    resize={ event => this.resizeSection(event, index, handle) }
                                    index={ index }
                                    handle={ handle }
                                    expanded={ expanded }
                                />
                                : null }
                            <div className={ style.risk_indication_content_wrapper }>
                                { riskContent }
                            </div>
                        </React.Fragment>
                        }
                        classes={{
                            root: style.consultantTextSection,
                            roundIcon: {
                                roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                            },
                            roundIconFigureWrapper__withExpandIcon: style.consultantTextSection__roundIconFigureWrapperExpandIcon
                        }}
                    />
                </div>;
                break;

            case ReportSections.TEAM_ROLES_DEVELOPMENT:
                fitMatch = ReportHelper.findScoreObjectInArrayByName('fit-match', section.scores, {});
                personalityString = ReportHelper.getTeamRolesDevelopmentFooterString(TeamRolePerspectives.PERSONALITY, section.scores, i18nParticipation);
                ambitionString = ReportHelper.getTeamRolesDevelopmentFooterString(TeamRolePerspectives.AMBITION, section.scores, i18nParticipation);

                gridItem = <div
                    role="button"
                    tabIndex="-1"
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    onClick={ () => {
                        showReportOverlay(ReportSections.TEAM_ROLES_DEVELOPMENT);
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                    data-cy={ `summary ${handle} viewall` }
                >

                    <SectionBlock
                        classes={{
                            root: `${style.scoreBlockWhite} ${style.sectionBlock__teamRolesDevelopment}`,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__teamRoles
                            }
                        }}>

                        <ReportSectionHeading
                            title={ section.title }
                            iconType={ 'desktop' }
                            iconText={ basedOnText }
                            introHeading={ false }
                            classes={{
                                roundIcon: {
                                    root: style.scoreRoundIcon__root,
                                    roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                }
                            }}
                        />

                        { section.introduction
                            ? <ExpandText
                                text={ section.introduction }
                                classes={{
                                    root: style.expandText__reportSection
                                }}
                                resize={ event => this.resizeSection(event, index, handle) }
                                index={ index }
                                handle={ handle }
                                expanded={ expanded }
                            />
                            : null }

                        <div className={ style.teamRolesDevelopment__results }>
                            <PersonaFitRozet
                                key={ `rozet_${this.personaFitRozetIndex++}` }
                                classes={ style.teamRolesDevelopmentRozetLeft }
                                scores={ ReportHelper.extractScoresForTeamRolePerspective(section.scores, TeamRolePerspectives.PERSONALITY, PersonaFitRoles) }
                                i18nParticipation={ i18nParticipation }
                            >
                                <aside className={ style.teamRolesAside } >
                                    <header>{ i18nParticipation.report_personality }</header>
                                    <p dangerouslySetInnerHTML={{ __html: i18nParticipation.report_rozet_aside_personality_a }} />
                                </aside>
                            </PersonaFitRozet>

                            <FitMatch
                                score={ fitMatch ? fitMatch.value : null }
                                classes={ { root: style.fitMatch }}
                            >
                                <aside className={ `${style.teamRolesAside} ${style.fitMatch__aside}` }>
                                    <header>{ i18nParticipation.report_rozet_fit_match_title }</header>
                                    <p dangerouslySetInnerHTML={{ __html: fitMatch.definition }} />
                                </aside>
                            </FitMatch>

                            <PersonaFitRozet
                                key={ `rozet_${this.personaFitRozetIndex++}` }
                                classes={ style.teamRolesDevelopmentRozetRight }
                                scores={ ReportHelper.extractScoresForTeamRolePerspective(section.scores, TeamRolePerspectives.AMBITION, PersonaFitRoles) }
                                i18nParticipation={ i18nParticipation }
                            >
                                <aside className={ `${style.teamRolesAside} ${style.teamRolesDevelopmentAside__right}` }>
                                    <header>{ i18nParticipation.report_ambition }</header>
                                    <p dangerouslySetInnerHTML={{ __html: i18nParticipation.report_rozet_aside_ambition_a }} />
                                </aside>
                            </PersonaFitRozet>
                        </div>

                        <p className={ style.teamRolesDevelopment__footer }>
                            { i18nParticipation.report_rozet_footer_part_a }
                            <span dangerouslySetInnerHTML={{ __html: personalityString }} />
                            { i18nParticipation.report_rozet_footer_part_c }
                            <span dangerouslySetInnerHTML={{ __html: ambitionString }} /> .
                        </p>
                    </SectionBlock>

                </div>;
                break;

            case ReportSections.TEAM_ROLES:
                gridItem = <div
                    role="button"
                    tabIndex="-1"
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    onClick={ () => {
                        showReportOverlay(ReportSections.TEAM_ROLES);
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                    data-cy={ `summary ${handle} viewall` }
                >

                    <SectionBlock
                        classes={{
                            root: `${style.scoreBlockWhite} ${style.sectionBlock__teamRoles}`,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__teamRoles
                            }
                        }}>

                        <ReportSectionHeading
                            title={ section.title }
                            iconType={ 'desktop' }
                            iconText={ basedOnText }
                            introHeading={ false }
                            classes={{
                                roundIcon: {
                                    root: style.scoreRoundIcon__root,
                                    roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                }
                            }}
                        />

                        { section.introduction
                            ? <ExpandText
                                text={ section.introduction }
                                classes={{
                                    root: style.expandText__reportSection
                                }}
                                resize={ event => this.resizeSection(event, index, handle) }
                                index={ index }
                                handle={ handle }
                                expanded={ expanded }
                            />
                            : null }

                        <PersonaFitRozet
                            key={ `rozet_${this.personaFitRozetIndex++}` }
                            classes={ style.teamRolesPersonaFitRozetTop }
                            scores={ ReportHelper.extractScoresForTeamRolePerspective(section.scores, TeamRolePerspectives.PERSONALITY, PersonaFitRoles) }
                            i18nParticipation={ i18nParticipation }
                        >
                            <aside className={ style.teamRolesAside }>
                                <header>{ i18nParticipation.report_personality }</header>
                                <p dangerouslySetInnerHTML={{ __html: i18nParticipation.report_rozet_aside_personality_a }} />
                            </aside>
                        </PersonaFitRozet>
                        <PersonaFitRozet
                            key={ `rozet_${this.personaFitRozetIndex++}` }
                            classes={ style.teamRolesPersonaFitRozetBottom }
                            scores={ ReportHelper.extractScoresForTeamRolePerspective(section.scores, TeamRolePerspectives.AMBITION, PersonaFitRoles) }
                            i18nParticipation={ i18nParticipation }
                        >
                            <aside className={ `${style.teamRolesAside} ${style.teamRolesAside__right}` }>
                                <header>{ i18nParticipation.report_ambition }</header>
                                <p dangerouslySetInnerHTML={{ __html: i18nParticipation.report_rozet_aside_ambition_a }} />
                            </aside>
                        </PersonaFitRozet>
                    </SectionBlock>
                </div>;
                break;

            // appendixes (not attachments!)
            case ReportSections.WPQ:
            case ReportSections.WORKING_STYLES:

                gridItem = <div
                    key={ handle }
                    role="button"
                    tabIndex="-1"
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    onClick={ () => {
                        showReportOverlay(handle === ReportSections.WPQ ? ReportSections.WPQ : ReportSections.WORKING_STYLES);
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                    data-cy={ `summary ${handle} viewall` }
                >
                    <SectionBlock
                        classes={{
                            root: `${style.scoreBlockWhite} ${style.sectionBlock__withBadge}`,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__workingStyles
                            }
                        }}
                    >
                        <MultipleRankingSection
                            faIconString={ 'desktop' }
                            classes={{
                                root: style.multipleRankingSection,
                                output: style.multipleRankingSection__output,
                                roundIcon: {
                                    roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                },
                                contentWrapper: style.sectionBlock__noPadding
                            }}
                            title={ section.title }
                            iconText={ basedOnText }
                        >
                            { section.introduction
                                ? <ExpandText
                                    text={ section.introduction }
                                    classes={{
                                        root: style.expandText__reportSection
                                    }}
                                    resize={ event => this.resizeSection(event, index, handle) }
                                    index={ index }
                                    handle={ handle }
                                    expanded={ expanded }
                                />
                                : null }

                            { section.scores.map(
                                (wpqScore, i) => <AppendixScoreBarSection
                                    key={ `scale-${wpqScore.name}-${i}` }
                                    scoreValue={ wpqScore.score }
                                    scoreMin={ ScoreRange.APPENDIXES.MIN }
                                    scoreMax={ ScoreRange.APPENDIXES.MAX }
                                    lowLabel={ wpqScore.lowLabel }
                                    highLabel={ wpqScore.highLabel }
                                    scoreName={ wpqScore.name }
                                    displayScore={ wpqScore.scoreTypes
                                        ? ReportHelper.getScoreType(wpqScore.scoreTypes, ScoreDisplayTypes.NORM5, 1)
                                        : null }
                                    scoreBarType={ ScoreBarTypes.BAR_GRAPH }
                                    classes={ {
                                        low: style.lowLabel,
                                        high: style.highLabel,
                                        fill: style.multipleRankingSectionScoreBar__fill,
                                        blank: style.multipleRankingSectionScoreBar__blank,
                                        scoreBlock: style.multipleRankingSectionScoreBar__scoreBlock,
                                        categoryScore: style.multipleRankingSectionCategoryScore
                                    } }
                                />
                            ) }
                        </MultipleRankingSection>
                    </SectionBlock>
                </div>;
                break;

            case ReportSections.INFLUENCING_STYLES:
                gridItem = <div
                    key={ handle }
                    role="button"
                    tabIndex="-1"
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    onClick={ () => {
                        showReportOverlay(ReportSections.INFLUENCING_STYLES);
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                    data-cy={ `summary ${handle} viewall` }
                >
                    <SectionBlock
                        classes={{
                            root: `${style.scoreBlockWhite} ${style.sectionBlock__withBadge}`,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__influencingStyles
                            }
                        }}
                    >
                        <MultipleRankingSection
                            faIconString={ 'desktop' }
                            classes={{
                                root: style.multipleRankingSection,
                                output: style.multipleRankingSection__output,
                                roundIcon: {
                                    roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                },
                                contentWrapper: style.sectionBlock__noPadding
                            }}
                            title={ section.title }
                            iconText={ basedOnText }
                        >
                            { section.introduction
                                ? <ExpandText
                                    text={ section.introduction }
                                    classes={{
                                        root: style.expandText__reportSection
                                    }}
                                    resize={ event => this.resizeSection(event, index, handle) }
                                    index={ index }
                                    handle={ handle }
                                    expanded={ expanded }
                                />
                                : null }

                            { section.scores.map(
                                (influencingStyleScore, i) => <AppendixScoreBarSection
                                    key={ `scale-${influencingStyleScore.name}-${i}` }
                                    scoreValue={ influencingStyleScore.score }
                                    scoreMin={ ScoreRange.APPENDIXES.MIN }
                                    scoreMax={ ScoreRange.APPENDIXES.MAX }
                                    lowLabel={ influencingStyleScore.lowLabel }
                                    highLabel={ influencingStyleScore.highLabel }
                                    scoreName={ influencingStyleScore.name }
                                    displayScore={ influencingStyleScore.scoreTypes
                                        ? ReportHelper.getScoreType(influencingStyleScore.scoreTypes, ScoreDisplayTypes.NORM5, 1)
                                        : null
                                    }
                                    scoreBarType={ ScoreBarTypes.BAR_GRAPH }
                                    classes={ {
                                        low: style.lowLabel,
                                        high: style.highLabel,
                                        fill: style.multipleRankingSectionScoreBar__fill,
                                        blank: style.multipleRankingSectionScoreBar__blank,
                                        scoreBlock: style.multipleRankingSectionScoreBar__scoreBlock,
                                        categoryScore: style.multipleRankingSectionCategoryScore
                                    } }
                                />
                            ) }
                        </MultipleRankingSection>
                    </SectionBlock>
                </div>;
                break;

            case ReportSections.MOTIVES:
                gridItem = <div
                    key={ handle }
                    role="button"
                    tabIndex="-1"
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    onClick={ () => {
                        showReportOverlay(ReportSections.MOTIVES);
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                    data-cy={ `summary ${handle} viewall` }
                >
                    <SectionBlock
                        classes={{
                            root: `${style.scoreBlockWhite} ${style.sectionBlock__withBadge}`,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__motives
                            }
                        }}
                    >
                        <MultipleRankingSection
                            faIconString={ 'desktop' }
                            classes={{
                                root: style.multipleRankingSection,
                                output: style.multipleRankingSection__output,
                                roundIcon: {
                                    root: style.scoreRoundIcon__root,
                                    roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                },
                                contentWrapper: style.sectionBlock__noPadding
                            }}
                            title={ section.title }
                            iconText={ basedOnText }

                        >
                            { section.introduction
                                ? <ExpandText
                                    text={ section.introduction }
                                    classes={{
                                        root: style.expandText__reportSection
                                    }}
                                    resize={ event => this.resizeSection(event, index, handle) }
                                    index={ index }
                                    handle={ handle }
                                    expanded={ expanded }
                                />
                                : null }
                            { section.scores.map(
                                (motiveScore, i) => <AppendixScoreBarSection
                                    key={ `scale-${motiveScore.name}-${i}` }
                                    scoreValue={ motiveScore.score }
                                    scoreMin={ ScoreRange.APPENDIXES.MIN }
                                    scoreMax={ ScoreRange.APPENDIXES.MAX }
                                    lowLabel={ motiveScore.lowLabel }
                                    highLabel={ motiveScore.highLabel }
                                    scoreName={ motiveScore.name }
                                    displayScore={ motiveScore.scoreTypes
                                        ? ReportHelper.getScoreType(motiveScore.scoreTypes, ScoreDisplayTypes.NORM5, 1)
                                        : null
                                    }
                                    scoreBarType={ ScoreBarTypes.BAR_GRAPH }
                                    classes={ {
                                        low: style.lowLabel,
                                        high: style.highLabel,
                                        fill: style.multipleRankingSectionScoreBar__fill,
                                        blank: style.multipleRankingSectionScoreBar__blank,
                                        scoreBlock: style.multipleRankingSectionScoreBar__scoreBlock,
                                        categoryScore: style.multipleRankingSectionCategoryScore
                                    } }
                                />
                            ) }
                        </MultipleRankingSection>
                    </SectionBlock>
                </div>;
                break;

            case ReportSections.PERSONALITY:
                gridItem = <div
                    key={ handle }
                    role="button"
                    tabIndex="-1"
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ `${style.responsive_wide} ${style.personalitySectionWrapper}` }
                    onClick={ () => {
                        showReportOverlay(ReportSections.PERSONALITY);
                    }}
                    id={ handle }
                    data-cy={ `summary ${handle} viewall` }
                >
                    <SectionBlock
                        classes={{
                            root: `${style.scoreBlockWhite} ${style.sectionBlock__withBadge}`,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__personality
                            }
                        }}
                    >
                        <PersonalityGraph
                            scores={ section.scores }
                            participantLanguage={ reportData.participant.language }
                            i18nParticipation={ i18nParticipation }
                            introduction={ section.introduction ? section.introduction : null }
                            iconText={ basedOnText }
                            resizeSection={ this.resizeSection }
                            index={ index }
                            handle={ handle }
                            expanded={ expanded }
                            classes={{
                                roundIcon: {
                                    root: style.scoreRoundIcon__root,
                                    roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                },
                                contentWrapper: style.sectionBlock__noPadding
                            }}
                        />
                    </SectionBlock>
                </div>;
                break;

            case ReportSections.PERSONALITY_BUILDING_DOCKS:
                introHeading = reportData.sections.personalityBuildingDocks.introduction;
                profileIntroHeader = <p dangerouslySetInnerHTML={{ __html: introHeading }} />;

                gridItem = <div
                    role="button"
                    tabIndex="-1"
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                >
                    <SectionBlock
                        classes={{
                            root: style.scoreBlockWhite,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__buildingDocks
                            }
                        }}
                    >
                        <MultipleRankingSection
                            profileIntroHeader={ introHeading ? profileIntroHeader : null }
                            faIconString={ 'desktop' }
                            classes={{
                                root: style.multipleRankingSection,
                                output: style.multipleRankingSection__output,
                                roundIcon: {
                                    root: style.scoreRoundIcon__root,
                                    roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                },
                                contentWrapper: style.sectionBlock__noPadding
                            }}
                            title={ section.title }
                            iconText={ basedOnText }
                        >
                            { section.scores.map(
                                (personalityScore, i) => <AppendixScoreBarSection
                                    key={ `scale-${personalityScore.name}-${i}` }
                                    scoreValue={ personalityScore.score }
                                    scoreMin={ ScoreRange.APPENDIXES.MIN }
                                    scoreMax={ ScoreRange.APPENDIXES.MAX }
                                    lowLabel={ personalityScore.lowLabel }
                                    highLabel={ personalityScore.highLabel }
                                    scoreName={ personalityScore.name }
                                    scoreBarType={ ScoreBarTypes.NORMAL }
                                    displayScore={ personalityScore.score }
                                    classes={ {
                                        low: style.lowLabel,
                                        high: style.highLabel,
                                        fill: style.multipleRankingSectionScoreBar__fill,
                                        blank: style.multipleRankingSectionScoreBar__blank,
                                        scoreBlock: style.multipleRankingSectionScoreBar__scoreBlock,
                                        scoreBar: style.multipleRankingSectionScoreBar
                                    } }
                                />
                            ) }
                        </MultipleRankingSection>
                    </SectionBlock>
                </div>;
                break;

            case ReportSections.WORK_ENVIRONMENT:

                gridItem = <div
                    key={ handle }
                    role="button"
                    tabIndex="-1"
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_wide }
                    onClick={ () => {
                        showReportOverlay(ReportSections.WORK_ENVIRONMENT);
                    }}
                    id={ handle }
                    data-cy={ `summary ${handle} viewall` }
                >
                    <SectionBlock
                        classes={{
                            root: `${style.scoreBlockWhite} ${style.sectionBlock__withBadge}`,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__workEnvironment
                            }
                        }}
                    >
                        <div className={ style.work_environment_component_wrapper }>
                            <ReportSectionHeading
                                title={ section.title }
                                iconType={ 'desktop' }
                                iconText={ basedOnText }
                                introHeading={ false }
                                classes={{
                                    roundIcon: {
                                        root: style.scoreRoundIcon__root,
                                        roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                    }
                                }}
                            />

                            { section.introduction
                                ? <ExpandText
                                    text={ section.introduction }
                                    classes={{
                                        root: style.expandText__reportSection
                                    }}
                                    resize={ event => this.resizeSection(event, index, handle) }
                                    index={ index }
                                    handle={ handle }
                                    expanded={ expanded }
                                />
                                : null }

                            <div className={ style.work_environment_graph_wrapper }>
                                <WorkEnvironment
                                    i18n={ i18nParticipation }
                                    participantLanguage={ reportData.participant.language }
                                    workEnvironmentScores={ reportData.sections.workEnvironment.scores }
                                />
                            </div>
                        </div>
                    </SectionBlock>
                </div>;
                break;

            case ReportSections.REFLECTION:
                gridItem = <div
                    role="button"
                    tabIndex="-1"
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                >
                    <SectionBlock
                        classes={{
                            root: `${style.scoreBlockWhite} ${style.sectionBlock__reflection}`,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__reflection
                            }
                        }}>
                        <ReportSectionHeading
                            title={ section.title }
                            iconType={ 'user-edit' }
                            initials={ this.getInitials(reportData.participant) }
                            iconText={ basedOnText }
                            introHeading={ false }
                            classes={{
                                roundIcon: {
                                    root: style.scoreRoundIcon__root,
                                    roundIconFigureWrapper: style.reflection_heading_icon
                                }
                            }}
                        />

                        <Reflection
                            reflectionText={ section.text }
                            submitReflectionText={ this.props.submitReflectionText }
                            introductionText={ reportData.sections.reflection.introduction ||
                                i18nParticipation.report_reflection_with_exercises_introduction_text }
                            textareaPlaceholderText={ i18nParticipation.report_reflection_textarea_placeholder_text }
                            textareaPlaceholderTextActive={ i18nParticipation.report_reflection_textarea_placeholder_text_active }
                            forwardCoaching={ reportData.sections.reflection.forwardCoaching }
                            forWardCoachingTitle={ i18nParticipation.report_forward_coaching_title }
                            forWardCoachingText={ i18nParticipation.report_forward_coaching_text }
                            forWardCoachingLink={ this.props.forwardCoachingLink }
                            forWardCoachingLinkText={ i18nParticipation.report_forward_coaching_link_text }
                            allowedToEditReflection={ this.props.allowedToEditReflection }
                            saveInterval={ this.props.saveInterval }
                            isFinalized={ false } /* editing should always be possible, so this can be set statically */
                            textEditable={ true } /* sets whether text can be edited, if user is authorised to do so */
                            templateSlug={ null } /* not used by Reflection */
                            slug={ reportData.slug }
                            resizeSection={ this.resizeSection }
                            index={ index }
                            handle={ handle }
                            expanded={ expanded }
                        />
                    </SectionBlock>
                </div>;
                break;

            case ReportSections.REFLECTION_WITH_EXERCISES:
                gridItem = <div
                    role="button"
                    tabIndex="-1"
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_wide }
                    id={ handle }
                >
                    <SectionBlock
                        classes={{
                            root: `${style.scoreBlockWhite} ${style.sectionBlock__reflection}`,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons.badgeFigure__reflection
                            }
                        }}>
                        <ReportSectionHeading
                            title={ section.title }
                            iconType={ 'user-edit' }
                            initials={ this.getInitials(reportData.participant) }
                            iconText={ basedOnText }
                            introHeading={ false }
                            classes={{
                                roundIcon: {
                                    root: style.scoreRoundIcon__root,
                                    roundIconFigureWrapper: style.reflection_heading_icon
                                }
                            }}
                        />
                        <ExpandText
                            text={ section.introduction || i18nParticipation.report_reflection_with_exercises_introduction }
                            classes={{
                                root: style.expandText__reflectionWithExercises
                            }}
                            resize={ event => this.resizeSection(event, index, handle) }
                            index={ index }
                            handle={ handle }
                            expanded={ expanded }
                        />

                        { section.exercises.map(
                            (reflectionSection, i) =>
                                <React.Fragment key={ `reflectionExercise${i}` }>
                                    <h3>{ reflectionSection.questionText }</h3>
                                    <Reflection
                                        reflectionText={ reflectionSection.reflectionText }
                                        submitReflectionText={ this.props.submitReflectionExerciseText }
                                        introductionText={ null }
                                        textareaPlaceholderText={ reflectionSection.placeHolder }
                                        textareaPlaceholderTextActive={ i18nParticipation.report_reflection_textarea_placeholder_text_active }
                                        forwardCoaching={ section.exercises.length - 1 === i ? section.forwardCoaching : null } /* show forward coaching underneath the last exercise */
                                        forWardCoachingTitle={ i18nParticipation.report_forward_coaching_title }
                                        forWardCoachingText={ i18nParticipation.report_forward_coaching_text }
                                        forWardCoachingLink={ this.props.forwardCoachingLink }
                                        forWardCoachingLinkText={ i18nParticipation.report_forward_coaching_link_text }
                                        allowedToEditReflection={ this.props.allowedToEditReflection }
                                        saveInterval={ this.props.saveInterval }
                                        isFinalized={ false } /* editing should always be possible, so this can be set statically */
                                        textEditable={ true } /* sets whether text can be edited, if user is authorised to do so */
                                        templateSlug={ null } /* not used by Reflection */
                                        slug={ reflectionSection.handle }
                                        classes={{
                                            root: style.reflectionWithExercisesRoot,
                                            scrollableArea: style.reflectionWithExercisesScrollableArea
                                        }}
                                    />
                                </React.Fragment>
                        )}
                    </SectionBlock>
                </div>;
                break;

            case ReportSections.ABILITY_TO_CHANGE_CALCULATED:
                gridItem = <div
                    key={ handle }
                    role="button"
                    tabIndex="-1"
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    }}
                    className={ style.responsive_wide }
                    onClick={ () => {
                        showReportOverlay(ReportSections.ABILITY_TO_CHANGE_CALCULATED);
                    }}
                    data-cy={ `summary ${handle} viewall` }
                    id={ handle }
                >
                    <div className={ style.ability_to_change_calculated }>
                        <SectionBlock
                            classes={{
                                root: `${style.scoreBlockWhite} ${style.sectionBlock__withBadge}`,
                                badge: {
                                    root: style.badge__sectionBlock,
                                    figure: icons.badgeFigure__abilityToChange
                                }
                            }}
                        >
                            <MultipleRankingSection
                                faIconString={ 'desktop' }
                                classes={{
                                    root: style.multipleRankingSection,
                                    output: style.multipleRankingSection__output,
                                    roundIcon: {
                                        roundIconFigureWrapper: style.consultantTextSection__roundIconFigureWrapper
                                    },
                                    contentWrapper: style.sectionBlock__noPadding
                                }}
                                title={ section.title }
                                iconText={ basedOnText }
                            >
                                { section.introduction
                                    ? <ExpandText
                                        text={ section.introduction }
                                        classes={{
                                            root: style.expandText__reportSection
                                        }}
                                        resize={ event => this.resizeSection(event, index, handle) }
                                        index={ index }
                                        handle={ handle }
                                        expanded={ expanded }

                                    />
                                    : null }

                                <div>
                                    { section.scores.map(
                                        (abilityToChangeScore, i) => <AppendixScoreBarSection
                                            key={ `scale-${abilityToChangeScore.name}-${i}` }
                                            scoreValue={ abilityToChangeScore.score }
                                            scoreMin={ ScoreRange.APPENDIXES.MIN }
                                            scoreMax={ ScoreRange.APPENDIXES.MAX }
                                            lowLabel={ abilityToChangeScore.evaluation }
                                            highLabel={ abilityToChangeScore.highLabel }
                                            scoreName={ abilityToChangeScore.name }
                                            scoreBarType={ ScoreBarTypes.NORMAL }
                                            classes={{
                                                low: style.lowLabel,
                                                high: style.highLabel_abilityToChangeCalculated,
                                                fill: style.multipleRankingSectionScoreBar__fill,
                                                blank: style.multipleRankingSectionScoreBar__blank,
                                                scoreBlock: style.multipleRankingSectionScoreBar__scoreBlock__abilityToChangeCalculated,
                                                scoreBar: style.multipleRankingSectionScoreBar__abilityToChangeCalculated
                                            }}
                                        />
                                    ) }
                                </div>
                            </MultipleRankingSection>
                        </SectionBlock>
                    </div>
                </div>;
                break;

            case ReportSections.DEALING_WITH_RESISTANCE:
            {
                gridItem = (
                    <div
                        className={ style.responsive_wide }
                        key={ handle }
                        ref={ elementRef => this.gridItemRefs[handle] = elementRef }
                        id={ handle }
                    >
                        <DealingWithResistance
                            getInitials={ this.getInitials }
                            handle={ handle }
                            i18nParticipation={ i18nParticipation }
                            languageId={ languageId }
                            reportData={ reportData }
                            section={ section }
                        />
                    </div>
                );
                break;
            }

            case ReportSections.WORK_SAFELY:
            {
                const scoreRange = ScoreRange.WORK_SAFELY;

                const riskIndicationData = {
                    title: section.footerTitle,
                    score: section.score,
                    evaluation: section.evaluation,
                    translations: i18nParticipation
                };

                gridItem = <div
                    key={ handle }
                    ref={ elementRef => {
                        this.gridItemRefs[handle] = elementRef;
                    } }
                    className={ style.responsive_wide }
                    id={ handle }
                >
                    <SectionBlock
                        classes={ {
                            root: style.scoreBlockWhite,
                            badge: {
                                root: style.badge__sectionBlock,
                                figure: icons[`badgeFigure__${handle}`]
                            }
                        } }
                    >
                        <span className={ style.sectionBlock_subtitle }>{ basedOnText }</span>

                        <MultipleRankingSection
                            classes={ {
                                root: style.multipleRankingSection__workSafely,
                                output: style.multipleRankingSection__output,
                                roundIcon: {
                                    roundIconFigureWrapper: style.hidden
                                },
                                contentWrapper: style.sectionBlock__noPadding
                            } }
                            title={ section.title }
                            iconText={ '' }
                        >
                            { section.introduction
                                ? <ExpandText
                                    text={ section.introduction }
                                    classes={ {
                                        root: style.expandText__reportSection
                                    } }
                                    resize={ event => this.resizeSection(event, index, handle) }
                                    index={ index }
                                    handle={ handle }
                                    expanded={ expanded }
                                />
                                : null }
                            <WorkSafelyScores
                                section={section}
                                style={style}
                                scoreRange={scoreRange}
                                languageId={languageId}
                                i18nParticipation={i18nParticipation}
                                initials={this.getInitials(reportData.participant)}
                            />
                            <FooterBar data={ riskIndicationData } />
                        </MultipleRankingSection>
                    </SectionBlock>
                </div>;
                break;
            }

            case ReportSections.CRITICAL_THINKING:
            {
                gridItem = (
                    <div
                        className={ style.criticalThinkingSectionWrapper }
                        key={handle}
                        ref={ elementRef => {
                            this.gridItemRefs[handle] = elementRef;
                        }}
                        tabIndex="-1"
                        id={ handle }
                    >
                        <CriticalThinking
                            i18nParticipation={ i18nParticipation }
                            icon={icons.badgeFigure__criticalThinking}
                            section={ section }
                        />
                    </div>
                );
                break;
            }

            case ReportSections.LSR_CONCLUSION:
            {
                gridItem = (
                    <div
                        className={ style.lsrConclusionSectionWrapper }
                        key={handle}
                        ref={ elementRef => {
                            this.gridItemRefs[handle] = elementRef;
                        }}
                        tabIndex="-1"
                        id={ handle }
                    >
                        <LSRConclusion
                            i18nParticipation={ i18nParticipation }
                            icon={icons.badgeFigure__lsrConclusion}
                            section={ section }
                        />
                    </div>
                );
                break;
            }

            default:
                break;
        }

        return gridItem;
    }

    /**
     * Finds the highest score for the current perspective (personality or ambition) and returns it as a span element
     * @param {Object} section - object of score section
     * @param {string} perspective - perspective to search for (zijn/willen or personality/ambition)
     * @returns {Element} HTML element containing translated score name with the right styling applied
     */
    getHighestScoringLabelForTeamRolePerspective(section, perspective) {
        const i18nParticipation = this.props.i18nParticipation;
        const scoresToBeSorted = ReportHelper.extractScoresForTeamRolePerspective(section.scores, perspective, PersonaFitRoles);
        const sortedScores = Utils.numericallySortFieldInArray(scoresToBeSorted, 'value').reverse();
        const label = sortedScores[0].label;
        let roleNameLowerCased = null;

        if (i18nParticipation[`report_rozet_${label.toLowerCase()}`]) {
            roleNameLowerCased = i18nParticipation[`report_rozet_${label.toLowerCase()}`].toLowerCase();
        }
        return <span className={ style[label.toLowerCase()] }>
            { roleNameLowerCased || sortedScores[0].label || null }
        </span>;
    }

    render() {
        const { ReportGridConfig } = this.props;

        return (
            <GridLayout
                cols={ ReportGridConfig.layout.columns }
                rowHeight={ ReportGridConfig.layout.rowHeight }
                width={ ReportGridConfig.layout.width }
                margin={ [ReportGridConfig.layout.marginX, ReportGridConfig.layout.marginY] }
                layout={ this.state.gridLayout }
                compactType={ ReportGridConfig.layout.compactType }
                isDraggable={ false }
                isResizable={ false }
            >
                { this.buildReportGridContent() }
            </GridLayout>
        );
    }
}

export default Grid;
